import React, { useState } from 'react';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';
import { Button } from '../Button/Button';
import { PeriodSelector } from '../PeriodSelector/PeriodSelector';
import type { PeriodValue } from '../../lib/period';
import {
  channelRate,
  heroBooked,
  lastFourWeeks,
  perHundredContacted,
  productionScheduled,
  recallEyebrow,
  recallWaiting,
  showDeltas,
  weeklyAxisMax,
} from '../../recall-agent/derive';
import type {
  RcAgentData,
  RcChannel,
  RcEvent,
  RcEventTone,
  RcWeek,
} from '../../recall-agent/types';

export interface RecallAgentViewProps {
  data: RcAgentData;
  /** Whether Recall is on hold. Owned above this component (§6). */
  onHold: boolean;
  onOpenCampaigns?: () => void;
  onOpenSettings?: () => void;
  /** Confirmed hold, or resume. Resume never asks (§6). */
  onSetHold?: (onHold: boolean) => void;
  /** The report period (spec 41) — the selector sits first in the header. */
  period?: PeriodValue;
  onPeriodChange?: (value: PeriodValue) => void;
  onOpenTriage?: () => void;
  onViewAllLogs?: () => void;
  onViewAllRecalls?: () => void;
}

const SEGMENT_COLORS = ['var(--ai-3)', 'var(--success)', 'var(--gold)', 'var(--terracotta)', 'var(--ink-4)'];

const EVENT_TONE: Record<RcEventTone, string> = {
  held: 'var(--gold)',
  escalated: 'var(--terracotta)',
  agent: 'var(--ai-3)',
  outcome: 'var(--success)',
  batch: 'var(--ink-4)',
};

function Card({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        'rounded-[var(--r-lg)] border border-[var(--cream-2)] bg-[var(--cream-1)] p-4 shadow-[var(--sh-1)]',
        className,
      )}
    >
      {children}
    </div>
  );
}

function SectionHeading({ n, title, sub }: { n: string; title: string; sub: string }) {
  return (
    <div className="mb-2.5 flex flex-wrap items-baseline gap-2.5">
      <span className="font-[var(--font-mono)] text-[11px] font-bold text-[var(--ink-4)]">{n}</span>
      <h2 className="m-0 text-[14px] font-bold text-[var(--ink-0)]">{title}</h2>
      <span className="text-[12px] text-[var(--ink-4)]">{sub}</span>
    </div>
  );
}

/**
 * Bars, and nothing else (§4). The old chart drew a booking-rate line over them
 * with `24%` in the corner — a second, differently-computed rate beside the
 * hero's, which is exactly how the page ended up arguing with itself.
 */
function WeeklyChart({ weeks }: { weeks: RcWeek[] }) {
  const W = 720;
  const H = 150;
  const padB = 18;
  const maxVal = weeklyAxisMax(weeks);
  const bw = W / weeks.length;
  const barW = bw * 0.5;
  const y = (v: number) => H - padB - (v / maxVal) * (H - padB - 6);
  const gridlines = [0, maxVal / 2, maxVal];
  return (
    <svg
      viewBox={`0 0 ${String(W)} ${String(H)}`}
      className="w-full"
      role="img"
      aria-label={`Appointments booked per week over ${String(weeks.length)} weeks`}
    >
      {gridlines.map((t) => (
        <g key={t}>
          <line x1={0} x2={W} y1={y(t)} y2={y(t)} stroke="var(--cream-2)" strokeWidth={1} />
          <text x={2} y={y(t) - 2} fontSize={9} fill="var(--ink-4)" fontFamily="var(--font-mono)">
            {t}
          </text>
        </g>
      ))}
      {weeks.map((wk, i) => (
        <rect
          key={wk.label}
          x={i * bw + (bw - barW) / 2}
          y={y(wk.booked)}
          width={barW}
          height={H - padB - y(wk.booked)}
          rx={2}
          fill="color-mix(in srgb, var(--success) 55%, var(--cream-1))"
        >
          <title>{`${wk.label}: ${String(wk.booked)} booked`}</title>
        </rect>
      ))}
      {weeks.map((wk, i) =>
        i % 2 === 0 ? (
          <text
            key={`x-${wk.label}`}
            x={i * bw + bw / 2}
            y={H - 4}
            fontSize={9}
            textAnchor="middle"
            fill="var(--ink-4)"
            fontFamily="var(--font-mono)"
          >
            {wk.label}
          </text>
        ) : null,
      )}
    </svg>
  );
}

function ChannelRow({ ch }: { ch: RcChannel }) {
  const pct = channelRate(ch);
  return (
    <div className="border-t border-[var(--cream-2)] px-1 py-2.5">
      <div className="flex items-baseline justify-between text-[12.5px]">
        <span className="font-semibold text-[var(--ink-0)]">{ch.label}</span>
        <span className="text-[var(--ink-3)]">
          <span className="font-[var(--font-mono)] font-bold text-[var(--ink-0)]">{pct}%</span> ·{' '}
          {ch.booked.toLocaleString('en-US')} of {ch.sent.toLocaleString('en-US')}
        </span>
      </div>
      <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-[var(--cream-2)]">
        <div className="h-full rounded-full bg-[var(--success)]" style={{ width: `${String(pct)}%` }} />
      </div>
    </div>
  );
}

function ActivityRow({ e }: { e: RcEvent }) {
  return (
    <li className="flex items-baseline gap-2.5 py-1.5">
      <span className="mt-1 h-2 w-2 shrink-0 rounded-full" style={{ background: EVENT_TONE[e.tone] }} aria-hidden />
      <span className="flex-1 text-[12.5px] text-[var(--ink-1)]">
        {e.text}
        {/* Each event names its recall, so "14 emails held" is answerable (§5.3). */}
        {e.recall === undefined ? null : (
          <span className="ml-1.5 text-[11.5px] text-[var(--ink-4)]">· {e.recall}</span>
        )}
      </span>
      <span className="shrink-0 font-[var(--font-mono)] text-[11px] text-[var(--ink-4)]">{e.at}</span>
    </li>
  );
}

/**
 * The hold confirmation (§6). An **in-page** modal, never `window.confirm` — the
 * browser dialog cannot say what holding actually does, and what it does is the
 * only reason to show a dialog at all.
 */
function HoldModal({ onCancel, onConfirm }: { onCancel: () => void; onConfirm: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(20,22,28,0.35)] px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="hold-recall-title"
    >
      <div className="w-full max-w-[420px] rounded-[var(--r-lg)] border border-[var(--cream-2)] bg-[var(--cream-1)] p-5 shadow-[var(--sh-3)]">
        <h2 id="hold-recall-title" className="m-0 text-[16px] font-bold text-[var(--ink-0)]">
          Hold Recall?
        </h2>
        <p className="m-0 mt-2 text-[13px] leading-[1.6] text-[var(--ink-2)]">
          Nothing is sent from any recall until you resume. Replies are still answered, and calls already in Triage
          stay there.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="danger" size="sm" onClick={onConfirm}>
            Hold
          </Button>
        </div>
      </div>
    </div>
  );
}

export function RecallAgentView({
  data,
  onHold,
  onOpenCampaigns,
  onOpenSettings,
  onSetHold,
  period,
  onPeriodChange,
  onOpenTriage,
  onViewAllLogs,
  onViewAllRecalls,
}: RecallAgentViewProps) {
  const [asking, setAsking] = useState(false);
  const hero = data.hero;
  const booked = heroBooked(data);
  const totalSeg = booked === 0 ? 1 : booked;
  const waiting = recallWaiting();
  const deltas = showDeltas(data);

  return (
    <div className="cal-fade mx-auto min-h-0 w-full max-w-[1084px] flex-1 overflow-y-auto px-[30px] pb-16 pt-5">
      {/* header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          {/* Counted from the recall store, and it carries the hold state (§1). */}
          <div className="pc-eyebrow text-[var(--ink-3)]">{recallEyebrow()}</div>
          <h1 className="m-0 mt-0.5 font-[var(--font-display)] text-[24px] font-semibold tracking-[-0.02em] text-[var(--ink-0)]">
            {data.titleLead} <span className="text-[var(--ai-3)]">{data.titleAccent}</span>
          </h1>
          <p className="m-0 mt-0.5 text-[12.5px] text-[var(--ink-3)]">{data.subtitle}</p>
        </div>
        <div className="flex items-center gap-2">
          {period && onPeriodChange ? (
            <PeriodSelector variant="agent" value={period} onChange={onPeriodChange} startedISO="2026-03-03" />
          ) : null}
          <Button variant="primary" size="sm" iconLeft="refresh" onClick={onOpenCampaigns}>
            Open Recall campaigns
          </Button>
          <Button variant="secondary" size="sm" iconLeft="cog" onClick={onOpenSettings}>
            Settings
          </Button>
          {/* Resume does not ask — nobody needs confirming that work should restart. */}
          {onHold ? (
            <Button
              variant="primary"
              size="sm"
              iconLeft="play"
              onClick={() => {
                onSetHold?.(false);
              }}
            >
              Resume
            </Button>
          ) : (
            <Button
              variant="danger"
              size="sm"
              iconLeft="pause"
              onClick={() => {
                setAsking(true);
              }}
            >
              Hold
            </Button>
          )}
        </div>
      </div>

      {/* On hold — said on the page, not only in the eyebrow. */}
      {onHold ? (
        <section className="mt-4 flex flex-wrap items-center gap-3 rounded-[var(--r-lg)] border border-[color-mix(in_srgb,var(--terracotta)_35%,transparent)] bg-[var(--cream-0)] px-4 py-2.5">
          <Icon kind="pause" size={15} stroke={2} style={{ color: 'var(--terracotta)' }} />
          <span className="text-[13px] font-semibold text-[var(--terracotta)]">Recall is on hold</span>
          <span className="text-[12px] text-[var(--ink-2)]">
            Nothing is sending. Replies are still answered, and calls already in Triage stay there.
          </span>
        </section>
      ) : null}

      {/* The call queue — amber, because a human call is an opportunity, not an
          emergency. The count comes from Triage's own rows (§7). */}
      {waiting.count > 0 ? (
        <section
          aria-label="Calls waiting for a person"
          className="mt-4 flex flex-wrap items-center gap-3 rounded-[var(--r-lg)] border border-[color-mix(in_srgb,var(--gold)_35%,transparent)] bg-[var(--gold-tint)] px-4 py-2.5"
        >
          <Icon kind="phone-out" size={15} stroke={2} style={{ color: 'var(--gold)' }} />
          <span className="text-[13px] font-semibold text-[var(--gold)]">
            {waiting.count} calls waiting for a person
          </span>
          <span className="text-[12px] text-[var(--ink-2)]">{waiting.names}</span>
          <button
            type="button"
            className="ml-auto text-[12.5px] font-semibold text-[var(--gold)] hover:underline"
            onClick={onOpenTriage}
          >
            Open Calls →
          </button>
        </section>
      ) : null}

      {/* hero + the three cards */}
      <div className="mt-4 grid gap-3 lg:grid-cols-[1.7fr_1fr]">
        <Card className="border-l-[3px] border-l-[var(--brand-indigo)]">
          <div className="pc-eyebrow text-[var(--ink-3)]">Booked from recall</div>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="font-[var(--font-display)] text-[32px] font-semibold tracking-[-0.03em] text-[var(--ink-0)]">
              {booked.toLocaleString('en-US')}
            </span>
            <span className="text-[13px] text-[var(--ink-3)]">
              appointments · from {hero.contacted.toLocaleString('en-US')} patients contacted · {hero.caption}
            </span>
          </div>
          <div className="mt-3 flex h-3 overflow-hidden rounded-full">
            {hero.segments.map((seg, i) => (
              <div
                key={seg.label}
                style={{
                  width: `${String((seg.value / totalSeg) * 100)}%`,
                  background: SEGMENT_COLORS[i % SEGMENT_COLORS.length],
                }}
                title={seg.tooltip ?? `${seg.label}: ${String(seg.value)}`}
              />
            ))}
          </div>
          <div className="mt-2.5 flex flex-wrap gap-x-5 gap-y-1.5">
            {hero.segments.map((seg, i) => (
              <span
                key={seg.label}
                className="inline-flex items-center gap-1.5 text-[11.5px]"
                title={seg.tooltip}
              >
                <span
                  className="h-2 w-2 rounded-[2px]"
                  style={{ background: SEGMENT_COLORS[i % SEGMENT_COLORS.length] }}
                />
                <span
                  className={cn(
                    'text-[var(--ink-3)]',
                    seg.tooltip !== undefined && 'underline decoration-dotted underline-offset-2',
                  )}
                >
                  {seg.label}
                </span>
                <span className="font-[var(--font-mono)] font-bold text-[var(--ink-0)]">{seg.value}</span>
              </span>
            ))}
          </div>
          <div className="mt-3 flex flex-wrap items-baseline gap-x-6 gap-y-1 border-t border-[var(--cream-2)] pt-2 text-[12px] text-[var(--ink-3)]">
            {/* Divided, not written down. This line said 24 over a page that
                divided to 11 (§2). */}
            <span>
              Per 100 contacted →{' '}
              <span className="font-[var(--font-mono)] font-bold text-[var(--ink-0)]">
                {perHundredContacted(data)} booked
              </span>
            </span>
            <span>
              Production scheduled →{' '}
              <span className="font-[var(--font-mono)] font-bold text-[var(--ink-0)]">
                {productionScheduled(data)}
              </span>
            </span>
            <button
              type="button"
              className="ml-auto font-semibold text-[var(--ai-3)] hover:underline"
              onClick={onViewAllRecalls}
            >
              All recalls →
            </button>
          </div>
        </Card>
        <div className="grid gap-3">
          {data.sideCards.map((c) => (
            <Card key={c.key}>
              <div className="pc-eyebrow text-[var(--ink-3)]">{c.label}</div>
              <div className="mt-1 flex items-baseline gap-2">
                <span className="font-[var(--font-display)] text-2xl font-semibold tracking-[-0.03em] text-[var(--ink-0)]">
                  {c.value}
                </span>
                {/* Hidden under 60 days of history — never a change against
                    nothing (§3). */}
                {deltas && c.delta !== undefined ? (
                  <span className="text-[11.5px] font-semibold text-[var(--success)]">{c.delta}</span>
                ) : null}
              </div>
              <div className="mt-0.5 text-[11.5px] text-[var(--ink-4)]">{c.caption}</div>
            </Card>
          ))}
        </div>
      </div>

      {/* the chart */}
      <Card className="mt-3">
        <SectionHeading n="" title="Booked per week" sub="Last 13 weeks" />
        <WeeklyChart weeks={data.weeks} />
      </Card>

      {/* 01 channels — the old 01 (per-recall performance) is gone: the hero's
          segments are that view, and /recall is the full list (§5.1) */}
      <Card className="mt-3">
        <SectionHeading n="01" title="Channels" sub="Bookings by the last message before the patient booked." />
        {data.channels.map((c) => (
          <ChannelRow key={c.label} ch={c} />
        ))}
      </Card>

      {/* 02 live activity */}
      <Card className="mt-3">
        <div className="flex items-start justify-between">
          <SectionHeading
            n="02"
            title="Live activity"
            sub="The latest from every recall. The full record is in the Communication log."
          />
          <button
            type="button"
            className="text-[12px] font-semibold text-[var(--ai-3)] hover:underline"
            onClick={onViewAllLogs}
          >
            View all logs →
          </button>
        </div>
        <ul className="m-0 list-none p-0" aria-live="polite">
          {data.activity.map((e) => (
            <ActivityRow key={e.id} e={e} />
          ))}
        </ul>
      </Card>

      {asking ? (
        <HoldModal
          onCancel={() => {
            setAsking(false);
          }}
          onConfirm={() => {
            setAsking(false);
            onSetHold?.(true);
          }}
        />
      ) : null}
    </div>
  );
}

/** Exported for the §8 check that the chart's month and the hero agree. */
export { lastFourWeeks };
